// Qué cajas se le pueden ofrecer a un usuario al abrir caja.
//
// Vive fuera del formulario para poder probarse: importar el componente arrastra
// la librería de interfaz, que hoy el ejecutor de pruebas no transforma.
const normalizar = (texto) => (texto || "").toString().replace(/\s+/g, "").toUpperCase();

// Un usuario no-administrador solo debe ver las cajas de su Tienda/Grupo
// asignado (dato que llega de BC, ver UserForm.js). La Tienda se cruza por
// `caja.TPV` (código exacto, ej. "MALL DEL SOL" -- mismo valor que `tpv.No`
// y que el propio campo Tienda de BC, confirmado consultando en vivo el
// registro real de un empleado) -- NO por `caja.tpvNombre`, que en los datos
// reales queda igual a `caja.nombre` (bug de sincronización ajeno a este
// filtro, no el nombre de la tienda). El Grupo se cruza de forma
// normalizada contra `caja.nombre` porque el texto que llega de BC no
// siempre coincide con el mismo espaciado (ej. Grupo "CC LA Y" vs. caja
// "CAJA 1 CCLAY"); si esa comparación no encuentra ninguna caja (por texto
// que no siga ese patrón), se prioriza el filtro por tienda -- que sí usa un
// código exacto -- antes que dejar al cajero sin ninguna caja para abrir.
export const filtrarCajasPorUsuario = (cajas, currentUser) => {
  if (!currentUser?.Tienda) return cajas;

  const porTienda = cajas.filter((caja) => caja.TPV === currentUser.Tienda);

  // El recorte por tienda vale también para un supervisor, y no es una cuestión
  // de permisos sino de la clave de Business Central: allá un cajero se
  // identifica por (Tienda, No). Ofrecerle a SUPEXC --que es de MALL DEL SOL--
  // una caja de PVLAY produce una apertura que BC rechaza con "No existe el
  // cajero SUPEXC en la tienda PVLAY". Antes el supervisor las veía todas por
  // ser Admin, así que se podía elegir una combinación imposible y el aviso
  // llegaba recién al sincronizar. Del grupo sí queda libre: dentro de su
  // tienda, un supervisor abre cualquier caja.
  if (currentUser?.Admin) return porTienda;

  if (!currentUser?.Grupo) return porTienda;

  const grupoNormalizado = normalizar(currentUser.Grupo);
  const porGrupo = porTienda.filter((caja) => normalizar(caja.nombre).includes(grupoNormalizado));

  return porGrupo.length > 0 ? porGrupo : porTienda;
};

/**
 * Las tiendas que un usuario puede consultar en Traer Históricos.
 *
 * No es la misma regla que la de abrir caja, y la diferencia es deliberada:
 *
 * - **Abrir caja** recorta por tienda a todos, supervisor incluido, porque
 *   Business Central identifica al cajero por (Tienda, No) y una apertura en
 *   otra tienda la rechaza. Ahí el recorte evita crear algo imposible.
 * - **Traer Históricos** sólo lee. No hay nada que BC pueda rechazar, así que
 *   el supervisor la usa global --necesita revisar cualquier tienda-- y el
 *   cajero queda en la suya.
 *
 * Si el usuario no tiene tienda asignada no hay con qué recortar y las ve
 * todas. Si la tiene y no aparece entre las sincronizadas, la lista queda
 * vacía a propósito: es una restricción, y aflojarla ante un dato faltante la
 * volvería inútil justo cuando importa.
 */
export const filtrarTiendasPorUsuario = (tpvs, currentUser) => {
  if (currentUser?.Admin) return tpvs;
  if (!currentUser?.Tienda) return tpvs;

  return tpvs.filter((tpv) => tpv.No === currentUser.Tienda);
};

const tiendaNormalizada = (texto) =>
  String(texto ?? "")
    .trim()
    .toUpperCase();

/**
 * La tienda del cajero: la de su sesión o, si la sesión no la trae, la del
 * maestro de usuarios.
 *
 * La sesión vive en una cookie de dos semanas con el usuario tal como era al
 * entrar. Si entonces el maestro todavía no traía la tienda, la sesión sigue sin
 * ella, y sin tienda el recorte de cajas no recorta nada.
 */
export const tiendaDelCajero = (currentUser, usuarios = []) => {
  const deLaSesion = String(currentUser?.Tienda ?? "").trim();
  if (deLaSesion) return deLaSesion;
  const delMaestro = usuarios.find((usuario) => usuario?.No === currentUser?.No);
  return String(delMaestro?.Tienda ?? "").trim();
};

/**
 * La caja que el formulario puede dejar puesta de entrada, o null.
 *
 * El formulario proponía la última caja consultada en Traer Históricos sin
 * mirar si era de la tienda del cajero. Traer Históricos deja consultar
 * cualquier tienda al supervisor, así que el 27/10/2026, en Perú TEST, el
 * supervisor de PRIMAVERA consultó SANMIGUEL02 y al abrir caja la tenía puesta:
 * Business Central rechazó la apertura («No existe el cajero SUPEXCPRIM en la
 * tienda SAN MIGUEL2») y su cierre quedó reintentándose sin poder salir.
 */
export const cajaPropuesta = (cajasPermitidas, numeroDeCaja) =>
  numeroDeCaja && cajasPermitidas.some((caja) => caja.No === numeroDeCaja) ? numeroDeCaja : null;

/**
 * Por qué no se puede abrir esta caja con este cajero, o null si se puede.
 *
 * Es la última red, al guardar: Business Central identifica al cajero por
 * (Tienda, No) y rechaza una apertura en una tienda donde el cajero no existe,
 * pero eso sólo se sabe al sincronizar, cuando el turno ya se usó. Si no se
 * conoce alguna de las dos tiendas no se bloquea: no hay con qué comparar.
 */
export const motivoParaNoAbrir = ({
  cajero,
  tiendaDelCajero: tiendaCajero,
  caja,
  tiendaDeLaCaja,
}) => {
  const delCajero = tiendaNormalizada(tiendaCajero);
  const deLaCaja = tiendaNormalizada(tiendaDeLaCaja);
  if (!delCajero || !deLaCaja || delCajero === deLaCaja) return null;
  return `La caja ${caja} es de la tienda ${tiendaDeLaCaja} y el cajero ${cajero} es de la tienda ${tiendaCajero}. Business Central no aceptaría esta apertura: elige una caja de tu tienda o inicia sesión con un cajero de ${tiendaDeLaCaja}.`;
};
